import {skipToken} from "@reduxjs/toolkit/query";
import {
  connectUserProcessStream,
  extractErrorMessage,
  upsertUserProcess,
  useGetMyUserProcessesQuery,
  UserProcess,
  UserProcessStreamState,
} from "@ots-frontend/ots-data";
import {useContext, useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {AppContext} from "../app/AppContext";
import {useAuth} from "../app/AuthProvider";
import {findNewlyFinished} from "./process-ui";

/** How often the list is refetched while the live stream is down. */
export const PROCESS_POLL_INTERVAL_MS = 15_000;

export type UseUserProcessesOptions = {
  /** Keep a live stream open. Off in stories and anywhere without a server. */
  live?: boolean;
  /** Called once for each process that finishes while this is mounted. */
  onFinished?: (process: UserProcess) => void;
  /** Swaps the stream, for tests. */
  connect?: typeof connectUserProcessStream;
};

export type UseUserProcessesResult = {
  processes: UserProcess[];
  loading: boolean;
  error?: string;
  reload: () => void;
  streamState: UserProcessStreamState;
};

/**
 * The signed-in person's processes, kept current by the server's event stream, with polling as a fallback while the
 * stream is down.
 */
export const useUserProcesses = ({
  live = true,
  onFinished,
  connect = connectUserProcessStream,
}: UseUserProcessesOptions = {}): UseUserProcessesResult => {
  const app = useContext(AppContext);
  const {userId} = useAuth() ?? {};
  const dispatch = useDispatch();
  const [streamState, setStreamState] = useState<UserProcessStreamState>(live ? "connecting" : "closed");

  const query = useGetMyUserProcessesQuery(userId ? undefined : skipToken, {
    pollingInterval: live && streamState === "open" ? 0 : PROCESS_POLL_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  });

  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;
  const loadedRef = useRef(false);
  loadedRef.current = query.isSuccess;

  const baseQueryApi = app?.baseQueryApi;

  useEffect(() => {
    if (!live || !userId || !baseQueryApi) return;

    const connection = connect(baseQueryApi, {
      onProcess: (process) => dispatch(upsertUserProcess(process) as never),
      // the first refresh arrives as the stream opens; only refetch once a list is loaded
      onRefresh: () => {
        if (loadedRef.current) void refetchRef.current();
      },
      onStateChange: setStreamState,
    });

    return () => connection.close();
  }, [live, userId, baseQueryApi, connect, dispatch]);

  const processes = query.data ?? [];

  const previous = useRef<UserProcess[] | undefined>(undefined);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    if (!query.data) return;
    if (previous.current) {
      findNewlyFinished(previous.current, query.data).forEach((process) => onFinishedRef.current?.(process));
    }
    previous.current = query.data;
  }, [query.data]);

  return {
    processes,
    loading: query.isLoading,
    error: query.isError ? extractErrorMessage(query.error, "Network error") : undefined,
    reload: () => void query.refetch(),
    streamState: live ? streamState : "closed",
  };
};
